export interface IGtmDataLayerEvent {
    event: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export enum GtmEnvironment {
    DEV = "dev", // Used for anything but live environment.
    LIVE = "live", // Used ONLY for live environment.
}

export enum GtmDevice {
    MOBILE = "mobile",
    TABLET = "tablet",
    DESKTOP = "desktop",
}

export enum GtmCheckoutStep {
    ONE = 1, // basket page
    TWO = 2, // checkout panel open
    THREE = 3 // click "go to payment"
}

export interface IGtmGenericVariables {
    ogLocation: string; // "https://www.montanafurniture.com/ - original location should always be static
    userID?: string; // "ABC12345" - User ID if present
    deviceType: GtmDevice;
    environment: GtmEnvironment;
    pageUrl: string;
    pagePath: string;
    bu: string;
}

export interface IGtmVirtualPageView extends IGtmDataLayerEvent {
    event: "virtualPageView";
    pageUrl: string;
    pagePath: string;
    pageTitle: string;
    anonymousID: string; // Cart Guid
}

export interface IGtmProductImpression extends IGtmDataLayerEvent {
    event: "eec_productImpression";
    ecommerce: {
        currencyCode: string;
        impressions: IGtmProductFromList[];
    };
}

export interface IGtmProductClick extends IGtmDataLayerEvent {
    event: "eec_productClick";
    ecommerce: {
        currencyCode: string;
        click: {
            actionField: {
                list: string;
            };
            products: IGtmProductFromList[];
        };
    };
}

export interface IGtmProductView extends IGtmDataLayerEvent {
    event: "eec_productView";
    ecommerce: {
        currencyCode: string;
        detail: {
            products: IGtmProduct[];
        };
    };
}

export interface IGtmAddToCart extends IGtmDataLayerEvent {
    event: "eec_addToCart";
    ecommerce: {
        currencyCode: string;
        add: {
            products: IGtmProductWithQuantity[];
        };
    };
}

export interface IGtmRemoveFromCart extends IGtmDataLayerEvent {
    event: "eec_removeFromCart";
    ecommerce: {
        currencyCode: string;
        add: {
            products: IGtmProductWithQuantity[];
        };
    };
}

export interface IGtmCheckoutStep extends IGtmDataLayerEvent {
    event: "eec_checkout";
    ecommerce: {
        currencyCode: string;
        checkout: {
            actionField: {
                step: GtmCheckoutStep;
            };
            products: IGtmProductWithQuantity[];
        };
    };
}

export interface IGtmCheckoutOption extends IGtmDataLayerEvent {
    event: "eec_checkoutOption";
    ecommerce: {
        checkout_option: {
            actionField: {
                step: GtmCheckoutStep;
                option: string;
            };
        };
    };
}

export interface IGtmCheckoutComplete extends IGtmDataLayerEvent {
    event: "eec_purchase";
    ecommerce: {
        currencyCode: string;
        checkout: {
            actionField: {
                id: string; // Transaction ID. Required for purchases and refunds.
                revenue: number; // Total transaction value
                shipping: number; // Leveringsgebyr
            };
            products: IGtmProductWithQuantity[];
        };
    };
}

export interface IGtmPurchaseActionField {
    id: string; // Transaction ID. Required for purchases and refunds.
    revenue: number; // Total transaction value
    tax: number; // Value of tax
    shipping: number; // Delivery fee
    affiliation: string; // If any
    coupon: string; // Vouchers
}

export interface IGtmPurchase extends IGtmDataLayerEvent {
    event: "eec_purchase";
    ecommerce: {
        currencyCode: string;
        purchase: {
            actionField: IGtmPurchaseActionField;
            products: IGtmProductWithQuantity[];
        };
    };
}


export interface IGtmCustomEvent extends IGtmDataLayerEvent {
    event: "gaEvent";
    eventCategory: string;
    eventAction: string;
    eventLabel?: string;
}

export interface IGtmProductWithQuantity extends IGtmProduct {
    quantity: number;
}

export interface IGtmProductFromList extends IGtmProduct {
    list: string;
    position: number;
}

export interface IGtmProduct {
    name: string; // "Extended wire"
    id: string; // "002001"
    price: number; // 87.44 - without TAX/VAT.
    category?: string;
    variant?: string; // "black"
    ean?: string;
}

export type WindowWithDL = typeof window & { dataLayer: Array<IGtmDataLayerEvent | IGtmGenericVariables | { [key: string]: string }> };
